import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import './ErrorStats.css';

const WINDOWS = [1, 7, 30] as const;

export function ErrorStats() {
  const [days, setDays] = useState<number>(7);
  const { data, isLoading, error } = useQuery({
    queryKey: ['errorStats', days],
    queryFn: () => adminApi.getErrorStats(days),
  });

  const maxCount = data?.byCode[0]?.count ?? 1;

  return (
    <div className="settings-section error-stats">
      <div className="section-header">
        <h2>LLM Error Stats</h2>
        <p className="section-description">
          Aggregated from typed <code>errorRecords</code> on model outputs
          (taxonomy). Grouped by error code, provider, and stage.
        </p>
      </div>

      <div className="error-stats-controls">
        {WINDOWS.map(w => (
          <button
            key={w}
            className={`window-btn ${days === w ? 'active' : ''}`}
            onClick={() => setDays(w)}
          >
            {w === 1 ? '24h' : `${w}d`}
          </button>
        ))}
      </div>

      {isLoading && <p>Loading…</p>}
      {error && <p className="error-text">Failed to load error stats.</p>}

      {data && (
        <>
          <p className="error-stats-total">
            <strong>{data.totalErrors.toLocaleString()}</strong> error record(s) in the last {data.windowDays}d
          </p>

          {data.byCode.length === 0 ? (
            <p className="error-stats-empty">No errors recorded in this window. 🎉</p>
          ) : (
            <>
              <div className="error-code-bars">
                {data.byCode.map(({ code, count }) => (
                  <div key={code} className="error-code-bar-row">
                    <span className={`error-code-label code-${code}`}>{code}</span>
                    <div className="error-code-bar-track">
                      <div className="error-code-bar-fill" style={{ width: `${(count / maxCount) * 100}%` }} />
                    </div>
                    <span className="error-code-count">{count.toLocaleString()}</span>
                  </div>
                ))}
              </div>

              <table className="error-stats-table">
                <thead>
                  <tr><th>Code</th><th>Provider</th><th>Stage</th><th>Count</th></tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i}>
                      <td><span className={`error-code-label code-${r.code}`}>{r.code}</span></td>
                      <td>{r.provider}</td>
                      <td>{r.stage}</td>
                      <td>{r.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </>
      )}
    </div>
  );
}
